import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CheckCircle, AlertCircle, Loader } from 'lucide-react'
import { Logo } from './Logo'
import { useAuth } from '../AuthProvider'

export const AuthConfirm: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()

  useEffect(() => {
    // If user is already authenticated, redirect to dashboard
    if (user) {
      console.log('🔐 AuthConfirm: User already authenticated, redirecting to dashboard')
      navigate('/qr/dash', { replace: true })
      return
    }

    const handleAuthConfirm = async () => {
      try {
        console.log('🔐 AuthConfirm: Starting token verification...')
        console.log('🔐 AuthConfirm: Current URL:', window.location.href)
        console.log('🔐 AuthConfirm: Search string:', window.location.search)
        
        // Get token parameters from URL
        const token_hash = searchParams.get('token_hash')
        const type = searchParams.get('type')
        const token = searchParams.get('token') // legacy magiclink param
        const code = searchParams.get('code')
        
        // Also check the raw URL as a fallback
        const urlParams = new URLSearchParams(window.location.search)
        const fallbackTokenHash = urlParams.get('token_hash')
        const fallbackType = urlParams.get('type')
        
        console.log('🔐 AuthConfirm: URL params:', { 
          token_hash: token_hash ? 'present' : 'missing', 
          type,
          token: token ? 'present' : 'missing',
          code: code ? 'present' : 'missing',
          fallbackTokenHash: fallbackTokenHash ? 'present' : 'missing',
          fallbackType,
          allParams: Object.fromEntries(searchParams.entries()),
          allFallbackParams: Object.fromEntries(urlParams.entries())
        })
        
        // Use fallback if primary method fails
        const finalTokenHash = token_hash || fallbackTokenHash
        const finalType = type || fallbackType

        // Parse hash tokens (implicit flow)
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')

        // PKCE: Exchange code for session if present
        if (code) {
          console.log('🔐 AuthConfirm: Attempting PKCE exchange...')
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)
          console.log('🔐 AuthConfirm: PKCE exchange result:', {
            user: data.user ? 'verified' : 'null',
            session: data.session ? 'created' : 'null',
            error: error?.message,
          })
          if (error) throw error
          if (!data.user || !data.session) throw new Error('Authentifizierung fehlgeschlagen (PKCE)')
        } else if (accessToken && refreshToken) {
          // Implicit tokens in hash
          console.log('🔐 AuthConfirm: Using implicit tokens from hash')
          const { data, error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          console.log('🔐 AuthConfirm: setSession result:', { session: data.session ? 'created' : 'null', error: error?.message })
          if (error) throw error
          if (!data.session) throw new Error('Authentifizierung fehlgeschlagen (implicit)')
        } else {
          // Magic link via token_hash
          if (finalTokenHash) {
            console.log('🔐 AuthConfirm: Attempting verifyOtp with token_hash (email)')
            const { data, error } = await supabase.auth.verifyOtp({
              token_hash: finalTokenHash,
              type: 'email',
            })
            console.log('🔐 AuthConfirm: Verification result:', {
              user: data.user ? 'verified' : 'null',
              session: data.session ? 'created' : 'null',
              error: error?.message,
            })
            if (error) throw error
            if (!data.user || !data.session) throw new Error('Authentifizierung fehlgeschlagen')
          } else if (token) {
            console.log('🔐 AuthConfirm: Attempting verifyOtp with token (email, legacy)')
            const { data, error } = await supabase.auth.verifyOtp({
              token,
              type: 'email',
            } as any)
            console.log('🔐 AuthConfirm: Verification (legacy token) result:', {
              user: data.user ? 'verified' : 'null',
              session: data.session ? 'created' : 'null',
              error: error?.message,
            })
            if (error) throw error
            if (!data.user || !data.session) throw new Error('Authentifizierung fehlgeschlagen (legacy token)')
          } else {
            console.error('🔐 AuthConfirm: Missing token parameters for magic link flow')
            throw new Error('Ungültiger Authentifizierungs-Link')
          }
        }

        // Success → redirect
        setSuccess(true)
        setTimeout(() => navigate('/qr/dash', { replace: true }), 500)

      } catch (err) {
        console.error('🔐 AuthConfirm: Error:', err)
        setError(err instanceof Error ? err.message : 'Unbekannter Fehler')
      } finally {
        setLoading(false)
      }
    }

    handleAuthConfirm()
  }, [searchParams, navigate, user])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" data-testid="auth-confirm-loading">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
        {/* Logo */}
        <div className="mb-8">
          <Logo size={60} className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">BeyondPages</h1>
        </div>

        {loading && (
          <div className="space-y-4">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <Loader className="h-6 w-6 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-lg font-medium text-gray-900">
              Authentifizierung wird überprüft...
            </h2>
            <p className="text-sm text-gray-600">
              Bitte warten Sie einen Moment.
            </p>
          </div>
        )}

        {success && (
          <div className="space-y-4">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-lg font-medium text-gray-900">
              Erfolgreich angemeldet!
            </h2>
            <p className="text-sm text-gray-600">
              Sie werden zum Dashboard weitergeleitet...
            </p>
          </div>
        )}

        {error && (
          <div className="space-y-4">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-lg font-medium text-gray-900">
              Authentifizierung fehlgeschlagen
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {error}
            </p>
            <button
              onClick={() => navigate('/qr/login')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Zurück zur Anmeldung
            </button>
          </div>
        )}
      </div>
    </div>
  )
} 
